import {ImprovementScore} from "./improvement-score"
import {ImprovementScoreItem} from "./improvement-score-item"
import {toAmount, toFormattedNumber} from "../../../components-shared/formatted-number/formatted-number"
import {SpecificImprovementScoreItem} from "../climate-advice"
import React, {useState} from "react"
import {useServices} from "../../../services/services"
import {FormImprovementInputProps, FormImprovementNumberInput, NumericValueAndValidationResult} from "./form-improvement-input"
import {ComputeNumericValue, getInitialComputeNumericValue} from "./compute-value"
import {WithVerticalSpacing} from "../../../components-shared/spacing/with-vertical-spacing"
import {HeadingSmall} from "../../../components-shared/heading/heading"
import {formatAmount, formatImpact} from "../../dashboard/transactions/transaction-amount/transaction-amount"
import {getDurationFromYears} from "../../../util/date-utils"
import {ImprovementInputParameters} from "../../../services/api/improvements-api"
import {validatePositiveNumber, validatePositiveNumberMax} from "./validation"

export interface SecondHandClothesImprovementScore extends ImprovementScore {
  currentClothesCost: number
  currentSecondHandFactor: number
  desiredSecondHandFactor: number
  currentSecondHandClothesCost: number
  currentNewClothesCost: number
  secondHandClothesCost: number
  newClothesCost: number
}

export class SecondHandClothesImprovementScoreItem implements ImprovementScoreItem {
  constructor(public improvementScore: SecondHandClothesImprovementScore) {

  }

  get title(): string {
    return "Tweedehands kleding kopen"
  }

  get explanation(): string {
    return `Gebaseerd op ${toFormattedNumber((this.improvementScore.desiredSecondHandFactor - this.improvementScore.currentSecondHandFactor) * 100, 0)}% van je nieuwe kleding tweedehands kopen (kijkend naar je huidige uitgaven van ${toAmount(-this.improvementScore.currentClothesCost, 0)} per jaar aan kleding)`
  }
}

export interface SecondHandClothesFormProps {
  improvementScoreItem: SecondHandClothesImprovementScoreItem
  updateImprovementScoreItem: (newImprovementScoreItem: SpecificImprovementScoreItem) => void
}

export const SecondHandClothesForm: React.FC<SecondHandClothesFormProps> = ({improvementScoreItem, updateImprovementScoreItem}) => {
  const services = useServices()
  const [viewModel, setViewModel] = useState<SecondHandClothesFormViewModel>(getInitialSecondHandClothesFormViewModel(improvementScoreItem))
  const controller = new AbortController()

  const handleNumberInputChange = (field: keyof SecondHandClothesValues) => async (numericValueAndValidationResult: NumericValueAndValidationResult) => {
    const updatedViewModel = {...viewModel}
    updatedViewModel.values[field].value = numericValueAndValidationResult.value
    if (numericValueAndValidationResult.resultType === "numeric") {
      (updatedViewModel.values[field] as ComputeNumericValue).numericValue = numericValueAndValidationResult.numericValue
    }
    updatedViewModel.values[field].partialValidationResult = numericValueAndValidationResult.partialValidationResult

    validateViewModel(updatedViewModel)
    setViewModel(updatedViewModel)

    if (updatedViewModel.isValid) {
      if (services === null) return

      const values = getValuesFromViewModel(updatedViewModel)

      const improvementScore = await services.improvementsApi.getImprovementScore(improvementScoreItem.improvementScore.improvement, values, controller.signal)
      updateImprovementScoreItem(new SecondHandClothesImprovementScoreItem(improvementScore as SecondHandClothesImprovementScore))
    }
  }

  const formImprovementInputProps = (key: keyof SecondHandClothesValues): FormImprovementInputProps => {
    return {
      description: viewModel.values[key].description,
      value: viewModel.values[key].value,
      onChange: handleNumberInputChange(key),
      validationResult: viewModel.values[key].validationResult
    }
  }

  return <div>
    <WithVerticalSpacing>
      <FormImprovementNumberInput {...formImprovementInputProps("currentClothesCost")}/>
      <FormImprovementNumberInput {...formImprovementInputProps("currentSecondHandFactor")}/>
      <FormImprovementNumberInput {...formImprovementInputProps("desiredSecondHandFactor")}/>

      <div>
        <HeadingSmall>Berekend resultaat</HeadingSmall>
        <table>
          <tbody>
            <tr>
              <td>CO<sub>2</sub>-reductie per jaar</td>
              <td>{toFormattedNumber(improvementScoreItem.improvementScore.co2ReductionPerYear, 0)} kg</td>
            </tr>
            {improvementScoreItem.improvementScore.financialInvestment > 0 && <tr>
              <td>Initiële investering</td>
              <td>{formatAmount(improvementScoreItem.improvementScore.financialInvestment, 0)} ({formatImpact(improvementScoreItem.improvementScore.co2Investment, 0)} CO<sub>2</sub>)</td>
            </tr>}
            {improvementScoreItem.improvementScore.financialPaybackPeriod !== null && (improvementScoreItem.improvementScore.financialPaybackPeriod > 0) && <tr>
              <td>Terugverdientijd (financieel)</td>
              <td>{getDurationFromYears(improvementScoreItem.improvementScore.financialPaybackPeriod)}</td>
            </tr>}
            {improvementScoreItem.improvementScore.co2PaybackPeriod !== null && (improvementScoreItem.improvementScore.co2PaybackPeriod > 0) && <tr>
              <td>Terugverdientijd (CO<sub>2</sub>)</td>
              <td>{getDurationFromYears(improvementScoreItem.improvementScore.co2PaybackPeriod)}</td>
            </tr>}
          </tbody>
        </table>
      </div>
    </WithVerticalSpacing>
  </div>
}

function getValuesFromViewModel(updatedViewModel: { isValid: boolean; values: SecondHandClothesValues }) {
  const values: ImprovementInputParameters = {
    currentClothesCost: updatedViewModel.values.currentClothesCost.numericValue === undefined ? undefined : - updatedViewModel.values.currentClothesCost.numericValue,
    currentSecondHandFactor: updatedViewModel.values.currentSecondHandFactor.numericValue === undefined ? undefined : updatedViewModel.values.currentSecondHandFactor.numericValue / 100,
    desiredSecondHandFactor: updatedViewModel.values.desiredSecondHandFactor.numericValue === undefined ? undefined : updatedViewModel.values.desiredSecondHandFactor.numericValue / 100,
  }

  return values
}

interface SecondHandClothesFormViewModel {
  isValid: boolean
  values: SecondHandClothesValues
}

type SecondHandClothesValues = {
  currentClothesCost: ComputeNumericValue,
  currentSecondHandFactor: ComputeNumericValue,
  desiredSecondHandFactor: ComputeNumericValue,
}

const getInitialSecondHandClothesFormViewModel = (improvementScoreItem: SecondHandClothesImprovementScoreItem): SecondHandClothesFormViewModel => {
  return {
    isValid: true,
    values: {
      currentClothesCost: getInitialComputeNumericValue(- improvementScoreItem.improvementScore.currentClothesCost, "Huidige uitgaven aan kleding"),
      currentSecondHandFactor: getInitialComputeNumericValue(100 * improvementScoreItem.improvementScore.currentSecondHandFactor, "Huidig percentage tweedehands kleding"),
      desiredSecondHandFactor: getInitialComputeNumericValue(100 * improvementScoreItem.improvementScore.desiredSecondHandFactor, "Gewenst percentage tweedehands kleding"),
    }
  }
}

const validateViewModel = (viewModel: SecondHandClothesFormViewModel): void => {
  viewModel.values.currentClothesCost.validationResult = viewModel.values.currentClothesCost.partialValidationResult.valid
    ? validatePositiveNumber(viewModel.values.currentClothesCost.numericValue)
    : viewModel.values.currentClothesCost.partialValidationResult

  viewModel.values.currentSecondHandFactor.validationResult = viewModel.values.currentSecondHandFactor.partialValidationResult.valid
    ? validatePositiveNumberMax(viewModel.values.currentSecondHandFactor.numericValue, 100)
    : viewModel.values.currentSecondHandFactor.partialValidationResult

  viewModel.values.desiredSecondHandFactor.validationResult = viewModel.values.desiredSecondHandFactor.partialValidationResult.valid
    ? validatePositiveNumberMax(viewModel.values.desiredSecondHandFactor.numericValue, 100)
    : viewModel.values.desiredSecondHandFactor.partialValidationResult

  viewModel.isValid = Object.keys(viewModel.values).every((key: string) => viewModel.values[key as keyof SecondHandClothesValues].validationResult.valid)
}
